import React, { useState, useEffect } from "react";
import { Modal, Table, InputNumber, Input, Select, Button, Space, Typography, message, Row, Col, Alert } from "antd";
import { useStore } from "../contexts/StoreContext";
import { useCompany } from "../contexts/CompanyContext";
import { useConfig } from "../contexts/ConfigContext";
import { useSecureRequest } from "../hooks/useSecureRequest";
import { IMovementRS } from "../models/storageModel";
import { InboxOutlined, UploadOutlined, CheckSquareOutlined } from "@ant-design/icons";
import { isExitMovement } from "../utils/reportUtils";

const { Text } = Typography;

interface FulfillMovementModalProps {
  visible: boolean;
  onClose: () => void;
  movement: Partial<IMovementRS> | null;
  onSuccess: () => void;
}

interface ItemReceiveRow {
  key: string;
  itemId: number;
  elementId?: number;
  name: string;
  presentation: string;
  totalQuantity: number;
  receivedQuantity: number;
  remainingQuantity: number;
  receiveNow: number;
}

export default function FulfillMovementModal({
  visible,
  onClose,
  movement,
  onSuccess,
}: Readonly<FulfillMovementModalProps>) {
  const { store } = useStore();
  const { company } = useCompany();
  const { config } = useConfig();
  const { securePost } = useSecureRequest();

  const [loading, setLoading] = useState(false);
  const [storehouseId, setStorehouseId] = useState<number | undefined>(undefined);
  const [carrier, setCarrier] = useState("");
  const [guideNumber, setGuideNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [itemsToReceive, setItemsToReceive] = useState<ItemReceiveRow[]>([]);

  const isExit = isExitMovement(movement?.document_type);

  useEffect(() => {
    if (movement && visible) {
      const defaultStorehouse = (isExit ? movement.storehouse_from_id : movement.storehouse_to_id) || store.list?.[0]?.id;
      setStorehouseId(defaultStorehouse);
      setCarrier("");
      setGuideNumber("");
      setNotes("");

      const rows: ItemReceiveRow[] = (movement.item_list || [])
        .filter((item: any) => item.register_type !== 'SERVICE' && item.track_inventory !== false)
        .map((item: any, idx: number) => {
        const total = Number(item.quantity || 0);
        const received = Number(item.quantity_received || 0);
        const remaining = Math.max(0, total - received);
        return {
          key: String(item.id || idx),
          itemId: Number(item.id),
          elementId: item.element_id ? Number(item.element_id) : undefined,
          name: item.item_name || item.name || "Ítem sin nombre",
          presentation: item.display || item.presentation || "UND",
          totalQuantity: total,
          receivedQuantity: received,
          remainingQuantity: remaining,
          receiveNow: remaining, // default receive all remaining
        };
      });
      setItemsToReceive(rows);
    }
  }, [movement, visible, store.list, isExit]);

  const handleQuantityChange = (key: string, val: number | null) => {
    const newVal = val === null ? 0 : val;
    setItemsToReceive((prev) =>
      prev.map((row) => (row.key === key ? { ...row, receiveNow: newVal } : row))
    );
  };

  const handleReceiveAll = () => {
    setItemsToReceive((prev) =>
      prev.map((row) => ({ ...row, receiveNow: row.remainingQuantity }))
    );
  };

  const handleReceiveNone = () => {
    setItemsToReceive((prev) => prev.map((row) => ({ ...row, receiveNow: 0 })));
  };

  const handleSubmit = async () => {
    if (!movement?.id) {
      message.error("ID de movimiento inválido");
      return;
    }
    if (!storehouseId) {
      message.error(isExit ? "Por favor seleccione el almacén origen" : "Por favor seleccione el almacén destino");
      return;
    }

    const payloadItems = itemsToReceive
      .filter((row) => row.receiveNow > 0)
      .map((row) => ({
        item_id: row.itemId,
        movement_item_id: row.itemId,
        element_id: row.elementId,
        quantity: row.receiveNow,
        quantity_received: row.receiveNow,
      }));

    if (payloadItems.length === 0) {
      message.warning(isExit ? "Debe ingresar al menos una cantidad mayor a 0 para despachar" : "Debe ingresar al menos una cantidad mayor a 0 para dar entrada");
      return;
    }

    setLoading(true);
    try {
      const res = await securePost("/inventory/movement/fulfill-movement", {
        company: company.current.uid,
        document_id: movement.id,
        movement_id: movement.id,
        storehouse_id: storehouseId,
        notes: notes || undefined,
        carrier: carrier || undefined,
        guide_number: guideNumber || undefined,
        remission_number: guideNumber || undefined,
        items: payloadItems,
        module: config.module,
      });

      if (res.status === 200 || res.success) {
        message.success(res.message || (isExit ? "Despacho de mercancía registrado con éxito" : "Entrada a bodega registrada con éxito"));
        onSuccess();
        onClose();
      } else {
        message.error(res.message || (isExit ? "Error al registrar el despacho" : "Error al registrar la entrada a bodega"));
      }
    } catch (err: any) {
      console.error("Error in fulfill-movement:", err);
      message.error(err?.message || (isExit ? "Error de conexión al despachar" : "Error de conexión al dar entrada"));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Ítem",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: ItemReceiveRow) => (
        <div>
          <Text strong>{text}</Text>
          <div style={{ fontSize: 11, color: "#8c8c8c" }}>{record.presentation}</div>
        </div>
      ),
    },
    {
      title: "Cant. Facturada",
      dataIndex: "totalQuantity",
      key: "totalQuantity",
      align: "center" as const,
      width: 110,
    },
    {
      title: isExit ? "Despachado Previo" : "Recibido Previo",
      dataIndex: "receivedQuantity",
      key: "receivedQuantity",
      align: "center" as const,
      width: 120,
    },
    {
      title: "Pendiente",
      dataIndex: "remainingQuantity",
      key: "remainingQuantity",
      align: "center" as const,
      width: 100,
      render: (val: number) => (
        <Text strong style={{ color: val > 0 ? "#faad14" : "#52c41a" }}>
          {val}
        </Text>
      ),
    },
    {
      title: isExit ? "Cant. A Despachar Ahora" : "Cant. A Recibir Ahora",
      key: "receiveNow",
      width: 170,
      render: (_: any, record: ItemReceiveRow) => (
        <InputNumber
          min={0}
          max={record.remainingQuantity}
          value={record.receiveNow}
          onChange={(val) => handleQuantityChange(record.key, val)}
          disabled={record.remainingQuantity <= 0}
          style={{ width: "100%" }}
        />
      ),
    },
  ];

  const totalToReceive = itemsToReceive.reduce((sum, r) => sum + (r.receiveNow || 0), 0);

  return (
    <Modal
      title={
        <Space>
          {isExit ? (
            <UploadOutlined style={{ color: "#fa8c16", fontSize: 20 }} />
          ) : (
            <InboxOutlined style={{ color: "#1890ff", fontSize: 20 }} />
          )}
          <span>{isExit ? "Registrar Despacho / Entrega de Mercancía" : "Dar Entrada Física a Bodega"}</span>
          <Text type="secondary" style={{ fontSize: 13 }}>
            ({movement?.document_number || "Doc"})
          </Text>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText={isExit ? `Confirmar Despacho (${totalToReceive} unid)` : `Confirmar Entrada (${totalToReceive} unid)`}
      cancelText="Cancelar"
      width={780}
      okButtonProps={{ disabled: totalToReceive <= 0 }}
    >
      <Alert
        message={isExit ? "Despacho y Salida Física de Almacén" : "Recepción de Mercancía en Almacén"}
        description={isExit
          ? "Esta acción registrará la remisión de salida física de la mercancía, descontando las unidades del inventario de la bodega y actualizando el estado de despacho/entrega."
          : "Esta acción ingresará el stock físico a la bodega seleccionada y generará automáticamente el comprobante contable de traslado de Mercancías en Tránsito (1499) a Inventario Disponible (1435)."}
        type={isExit ? "warning" : "info"}
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Row gutter={[16, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
            {isExit ? "Almacén / Bodega Origen:" : "Almacén / Bodega Destino:"}
          </label>
          <Select
            style={{ width: "100%" }}
            placeholder="Seleccione Almacén"
            value={storehouseId}
            onChange={(val) => setStorehouseId(val)}
          >
            {(store.list || []).map((s) => (
              <Select.Option key={s.id} value={s.id}>
                {s.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={12} md={6}>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
            Transportadora:
          </label>
          <Input
            placeholder="Ej: Servientrega"
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
          />
        </Col>
        <Col xs={12} md={6}>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
            No. Guía / Remisión:
          </label>
          <Input
            placeholder="Ej: GUIA-9871"
            value={guideNumber}
            onChange={(e) => setGuideNumber(e.target.value)}
          />
        </Col>
        <Col xs={24}>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
            {isExit ? "Observaciones de Despacho / Entrega:" : "Observaciones de Recepción:"}
          </label>
          <Input.TextArea
            rows={2}
            placeholder="Comentarios sobre el estado de la mercancía, empaque, remisión, etc."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Col>
      </Row>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <Text strong>{isExit ? "Detalle de Ítems a Despachar:" : "Detalle de Ítems a Recibir:"}</Text>
        <Space size="small">
          <Button size="small" icon={<CheckSquareOutlined />} onClick={handleReceiveAll}>
            {isExit ? "Despachar Todo el Saldo" : "Recibir Todo el Saldo"}
          </Button>
          <Button size="small" onClick={handleReceiveNone}>
            Limpiar Cantidades
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={itemsToReceive}
        pagination={false}
        size="small"
        bordered
      />
    </Modal>
  );
}
